import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Filter, Clock, AlertTriangle, CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';
import securityApi from '../api/securityApi';

/**
 * ScansPage - Displays all scans across all repositories
 *
 * TODO: Migrate to server-side filtering, sorting, and pagination
 *
 * KNOWN LIMITATIONS:
 * Currently all filtering, sorting, and pagination is performed client-side
 * after fetching ALL scan data. This approach has scalability issues:
 * - Browser may become unresponsive with 10,000+ scan records
 * - High memory usage as all data is held in state
 * - Initial load time increases linearly with total scans
 *
 * RECOMMENDED BACKEND ENDPOINT:
 * GET /api/scans?page=1&limit=20&repo_url=...&status=completed&start_date=...&end_date=...&sort_by=created_at&sort_order=desc
 *
 * The UI should pass filter/sort/pagination params to the API and only
 * render the paginated subset returned by the server.
 *
 * See also: securityApi.getAllScans() for related N+1 query limitations
 */
const ScansPage = () => {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [repositories, setRepositories] = useState([]);

  // Filter state
  const [filters, setFilters] = useState({
    repository: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'vulnerabilities'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const scansPerPage = 20;

  useEffect(() => {
    loadAllScans();
  }, []);

  const loadAllScans = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new getAllScans method that aggregates data
      const allScans = await securityApi.getAllScans();
      setScans(allScans);

      // Extract unique repositories for the filter dropdown
      const uniqueRepos = [...new Set(allScans.map(scan => scan.repo_url))];
      setRepositories(uniqueRepos);
    } catch (err) {
      console.error('Error loading scans:', err);
      setError('Failed to load scans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get scan date (API may use start_time or created_at)
  const getScanDate = (scan) => scan.start_time || scan.created_at;

  // Helper to get scan ID (API may use scan_id or id)
  const getScanId = (scan) => scan.scan_id || scan.id;

  // Apply filters
  const filteredScans = scans.filter(scan => {
    // Repository filter
    if (filters.repository && scan.repo_url !== filters.repository) {
      return false;
    }

    // Status filter (normalize: API returns 'complete', UI may filter 'completed')
    if (filters.status) {
      const scanStatus = scan.status?.toLowerCase();
      const filterStatus = filters.status.toLowerCase();
      // Handle 'complete' vs 'completed' mismatch
      const normalizedScanStatus = scanStatus === 'complete' ? 'completed' : scanStatus;
      if (normalizedScanStatus !== filterStatus) {
        return false;
      }
    }

    // Date range filter
    if (filters.startDate || filters.endDate) {
      const scanDate = new Date(getScanDate(scan));
      if (filters.startDate && scanDate < new Date(filters.startDate)) {
        return false;
      }
      if (filters.endDate) {
        // Set end date to end of day (23:59:59.999) to include all scans on that day
        const endOfDay = new Date(filters.endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (scanDate > endOfDay) {
          return false;
        }
      }
    }

    return true;
  });

  // Apply sorting
  const sortedScans = [...filteredScans].sort((a, b) => {
    let comparison = 0;

    if (sortBy === 'date') {
      comparison = new Date(getScanDate(a)) - new Date(getScanDate(b));
    } else if (sortBy === 'vulnerabilities') {
      const aTotal = (a.critical_count || 0) + (a.high_count || 0) + (a.medium_count || 0) + (a.low_count || 0);
      const bTotal = (b.critical_count || 0) + (b.high_count || 0) + (b.medium_count || 0) + (b.low_count || 0);
      comparison = aTotal - bTotal;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Pagination
  const indexOfLastScan = currentPage * scansPerPage;
  const indexOfFirstScan = indexOfLastScan - scansPerPage;
  const currentScans = sortedScans.slice(indexOfFirstScan, indexOfLastScan);
  const totalPages = Math.ceil(sortedScans.length / scansPerPage);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle order if clicking the same sort field
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc'); // Default to descending for new sort field
    }
  };

  const clearFilters = () => {
    setFilters({
      repository: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    // Handle both 'complete' and 'completed'
    if (statusLower === 'completed' || statusLower === 'complete') {
      return (
        <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm">
          <CheckCircle className="w-4 h-4" />
          Completed
        </span>
      );
    } else if (statusLower === 'running' || statusLower === 'in_progress' || statusLower === 'scanning') {
      return (
        <span className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-sm">
          <Clock className="w-4 h-4" />
          Running
        </span>
      );
    } else if (statusLower === 'failed' || statusLower === 'error') {
      return (
        <span className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full text-sm">
          <XCircle className="w-4 h-4" />
          Failed
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-gray-50 text-gray-600 border border-gray-200 rounded-full text-sm">
        {status || 'Unknown'}
      </span>
    );
  };

  const getRepositoryName = (repoUrl) => {
    try {
      const url = new URL(repoUrl);
      return url.pathname.substring(1); // Remove leading slash
    } catch {
      return repoUrl;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-700 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p>Loading scans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadAllScans}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Scans</h1>
          <p className="text-gray-600">View and filter scans across all repositories</p>
        </div>

        {/* Filters Section */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Repository Filter */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">Repository</label>
              <select
                value={filters.repository}
                onChange={(e) => handleFilterChange('repository', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Repositories</option>
                {repositories.map(repo => (
                  <option key={repo} value={repo}>
                    {getRepositoryName(repo)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="running">Running</option>
                <option value="in_progress">In Progress</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Start Date Filter */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          {(filters.repository || filters.status || filters.startDate || filters.endDate) && (
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Sort Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-gray-600">
            <ArrowUpDown className="w-4 h-4" />
            <span className="text-sm">Sort by:</span>
            <button
              onClick={() => handleSortChange('date')}
              className={`px-3 py-1 rounded text-sm ${
                sortBy === 'date'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSortChange('vulnerabilities')}
              className={`px-3 py-1 rounded text-sm ${
                sortBy === 'vulnerabilities'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Vulnerabilities {sortBy === 'vulnerabilities' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          </div>

          <div className="text-sm text-gray-600">
            Showing {currentScans.length} of {sortedScans.length} scans
          </div>
        </div>

        {/* Scans Table */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg overflow-hidden">
          {currentScans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {scans.length === 0 ? (
                <>
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No scans found</p>
                </>
              ) : (
                <p>No scans match the selected filters</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Repository
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scan Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Critical
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      High
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Medium
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Low
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentScans.map((scan) => {
                    const scanId = getScanId(scan);
                    const totalVulns = (scan.critical_count || 0) + (scan.high_count || 0) +
                                     (scan.medium_count || 0) + (scan.low_count || 0);

                    return (
                      <tr
                        key={scanId}
                        onClick={() => navigate(`/results/${scanId}`)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {getRepositoryName(scan.repo_url)}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {scan.repo_url}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(getScanDate(scan))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(scan.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-semibold ${
                            scan.critical_count > 0 ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {scan.critical_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-semibold ${
                            scan.high_count > 0 ? 'text-orange-600' : 'text-gray-400'
                          }`}>
                            {scan.high_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-semibold ${
                            scan.medium_count > 0 ? 'text-yellow-600' : 'text-gray-400'
                          }`}>
                            {scan.medium_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-semibold ${
                            scan.low_count > 0 ? 'text-blue-600' : 'text-gray-400'
                          }`}>
                            {scan.low_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-bold text-gray-900">
                            {totalVulns}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded ${
                      currentPage === pageNum
                        ? 'bg-teal-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScansPage;
